/**
 * One place that opens the Book sheet and the row actions, from anywhere: the rail's Book button, an
 * open position on Today, a tenant card, a Needs-your-attention item. The Book sheet is available to
 * the administrator, co-administrators and the scheduler — never to the crew or a viewer.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AppointmentRow } from '@shared/api-types';
import type { Role } from '@shared/roles';
import { Sheet } from '@/components/ui/sheet';
import { BookSheet, type BookOptions } from './BookSheet';
import { TechBookSheet } from '@/features/tech/TechBookSheet';
import { ActionSheet, type ActionKind } from './ActionSheet';
import { REQUEST_SERVICE, bookProgramsFor, type BookProgram } from '@shared/book-programs';
import { serviceDateKey } from '@shared/dates';
import { RequestSheet } from '@/features/volunteers/RequestSheet';
import { VolunteerHelpSheet } from '@/features/volunteers/VolunteerHelpSheet';
import { isHousekeepingPage, isTechPage } from '@/app/nav';

export const BOOKING_ROLES: readonly Role[] = ['admin', 'coadmin', 'scheduler', 'tech'];

/**
 * The program whose sheet Book opens for these options and this role (1.8.3) — never one the role may not book. 1.8.5:
 * a role that cannot book Housekeeping (the tech) opens on its first program (Tech Support).
 */
export function sheetFor(role: Role | null, opts: BookOptions): BookProgram {
  const mine = bookProgramsFor(role);
  const first = mine[0] ?? 'housekeeping';
  if ((opts.reschedule || opts.followUp) && mine.includes('housekeeping')) return 'housekeeping';
  const asked = opts.program ?? first;
  return mine.includes(asked) ? asked : first;
}

interface BookingCtx {
  canBook: boolean;
  /** Who is signed in (null: unknown) — the Move sheets use it to offer the administrator's second press. */
  role: Role | null;
  openBook: (opts: BookOptions, opener?: HTMLElement | null) => void;
  /** `office` (1.5.17): open it as "Office action" — Today's button for an administrator or a co-administrator. */
  openActions: (appt: AppointmentRow, kind?: ActionKind, opener?: HTMLElement | null, opts?: { office?: boolean; note?: string }) => void;
}

const Ctx = createContext<BookingCtx>({ canBook: false, role: null, openBook: () => undefined, openActions: () => undefined });

export function BookingProvider({ role, children }: { role: Role | null; children: ReactNode }) {
  const canBook = !!role && BOOKING_ROLES.includes(role);
  const [book, setBook] = useState<{ opts: BookOptions; n: number } | null>(null);
  const [action, setAction] = useState<{ appt: AppointmentRow; kind: ActionKind; office: boolean; note?: string; n: number } | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const seq = useRef(0);

  /**
   * Eli, 13 Oct 2026: "It should not be crisscrossing." Book pressed inside a program (its day, Planning, Schedule,
   * Tech Support, and since 17 Oct Events → Volunteer help) opens that program's sheet alone, with no row of the other
   * programs. Today, People, Volunteers and Look up keep the "what" row. A caller that names its program keeps it.
   */
  const here = useCallback((): BookOptions => {
    // read when Book is pressed (the router writes the browser's address), so no router is needed around this provider
    const path = typeof window === 'undefined' ? '' : window.location.pathname;
    if (isTechPage(path)) return { program: 'tech', lockProgram: true };
    if (isHousekeepingPage(path)) return { program: 'housekeeping', lockProgram: true };
    // Eli, 17 Oct 2026: Book on Events is Volunteer help alone (an event's volunteers), like every other program
    if (/^\/events(\/|$)/.test(path) && bookProgramsFor(role).includes('volunteer-help')) return { program: 'volunteer-help', lockProgram: true };
    // 26 Oct 2026 (D-1025-WIRING): Companion & Wellness and Reading & Services have addresses of their own — Book there is that program alone
    if (/^\/companion(\/|$)/.test(path)) return { program: 'companion', lockProgram: true };
    if (/^\/reading(\/|$)/.test(path)) return { program: 'reading', lockProgram: true };
    return {};
  }, [role]);
  const openBook = useCallback(
    (opts: BookOptions, el?: HTMLElement | null) => {
      if (!canBook) return;
      opener.current = el ?? (document.activeElement as HTMLElement | null);
      setAction(null);
      const inside = opts.program || opts.reschedule || opts.followUp ? {} : here();
      setBook({ opts: { ...inside, ...opts }, n: ++seq.current });
    },
    [canBook, here],
  );
  const openActions = useCallback(
    (appt: AppointmentRow, kind: ActionKind = 'menu', el?: HTMLElement | null, opts?: { office?: boolean; note?: string }) => {
      if (!canBook) return;
      opener.current = el ?? (document.activeElement as HTMLElement | null);
      if (kind === 'reschedule') {
        setBook({ opts: { reschedule: appt }, n: ++seq.current });
        return;
      }
      setAction({ appt, kind, office: !!opts?.office, ...(opts?.note ? { note: opts.note } : {}), n: ++seq.current });
    },
    [canBook],
  );
  const value = useMemo(() => ({ canBook, role, openBook, openActions }), [canBook, role, openBook, openActions]);
  /**
   * 1.8.3 (D-1.8.3-3): which sheet Book shows — the program asked for, when this role may book it. The scheduler
   * keeps Housekeeping and Tech Support and never reaches a volunteer sheet: anything else falls back to Housekeeping.
   * A reschedule or a "find a day" is always the Housekeeping sheet.
   */
  const sheet = book ? sheetFor(role, book.opts) : 'housekeeping';
  /** Book's "what" row: another program was pressed — its sheet opens with the tenant chosen so far kept. */
  const toProgram = (program: BookProgram, tenant: BookOptions['tenant']) => setBook({ opts: { tenant: tenant ?? null, program }, n: ++seq.current });
  /** Opened from inside a program (13 Oct): no "what" row — the sheets show it only when given this. */
  const pick = book?.opts.lockProgram ? undefined : toProgram;

  return (
    <Ctx.Provider value={value}>
      {children}
      <Sheet
        open={!!book}
        onOpenChange={(o) => {
          if (!o) {
            setBook(null);
          }
        }}
      >
        {book && sheet === 'tech' ? (
          <TechBookSheet key={book.n} tenant={book.opts.tenant ?? null} returnFocusTo={opener} onProgram={pick} onDone={() => setBook(null)} />
        ) : book && (sheet === 'companion' || sheet === 'reading') ? (
          <RequestSheet key={book.n} service={REQUEST_SERVICE[sheet]} tenant={book.opts.tenant ?? null} todayKey={serviceDateKey()} returnFocusTo={opener} onProgram={pick} onClose={() => setBook(null)} />
        ) : book && sheet === 'volunteer-help' ? (
          <VolunteerHelpSheet key={book.n} tenant={book.opts.tenant ?? null} todayKey={serviceDateKey()} returnFocusTo={opener} onProgram={pick ? (p) => pick(p, book.opts.tenant ?? null) : undefined} onDone={() => setBook(null)} />
        ) : book ? (
          <BookSheet
            key={book.n}
            opts={book.opts}
            role={role}
            returnFocusTo={opener}
            onProgram={pick}
            onDone={() => {
              setBook(null);
            }}
          />
        ) : null}
      </Sheet>
      <Sheet
        open={!!action}
        onOpenChange={(o) => {
          if (!o) {
            setAction(null);
          }
        }}
      >
        {action && role ? (
          <ActionSheet
            key={action.n}
            appt={action.appt}
            role={role}
            initial={action.kind}
            office={action.office}
            initialNote={action.note}
            returnFocusTo={opener}
            onDone={() => {
              setAction(null);
            }}
            onReschedule={() => {
              const a = action.appt;
              setAction(null);
              setBook({ opts: { reschedule: a }, n: ++seq.current });
            }}
          />
        ) : null}
      </Sheet>
    </Ctx.Provider>
  );
}

export function useBooking(): BookingCtx {
  return useContext(Ctx);
}
